import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

/**
 * Dictionary analytics — web parity for iOS. Mirrors the event names + props of
 * client-native's useTrackDictionaryEvents / useTrackWordStatusChange /
 * useTrackPhraseStatusChange (Dictionary.tsx lines 140/815/559/636/642/715/721/776).
 * Covers the full view-mode + status-change funnel, not just
 * viewed_words/viewed_phrases. Web call sites pass source="dictionary".
 */
export function useTrackDictionaryEvents() {
  const posthog = usePostHog();

  const trackDictionaryViewed = useCallback(() => {
    posthog?.capture("dictionary_viewed");
  }, [posthog]);

  const trackDictionaryViewModeChanged = useCallback(
    (type: "words" | "phrases", newMode: "learning" | "known") => {
      posthog?.capture("dictionary_view_mode_changed", {
        type,
        view_mode: newMode,
      });
    },
    [posthog],
  );

  const trackWordMovedToKnown = useCallback(
    (wordId: string, wordText: string, source = "dictionary") => {
      posthog?.capture("word_moved_to_known", {
        word_id: wordId,
        word_text: wordText,
        platform: "web",
        source,
      });
    },
    [posthog],
  );

  const trackWordMovedToLearning = useCallback(
    (wordId: string, wordText: string, source = "dictionary") => {
      posthog?.capture("word_moved_to_learning", {
        word_id: wordId,
        word_text: wordText,
        platform: "web",
        source,
      });
    },
    [posthog],
  );

  const trackWordDeleted = useCallback(
    (wordId: string, wordText: string, source = "dictionary") => {
      posthog?.capture("word_deleted", {
        word_id: wordId,
        word_text: wordText,
        platform: "web",
        source,
      });
    },
    [posthog],
  );

  const trackPhraseMovedToKnown = useCallback(
    (phraseId: string, phraseText: string, source = "dictionary") => {
      posthog?.capture("phrase_moved_to_known", {
        phrase_id: phraseId,
        phrase_text: phraseText,
        platform: "web",
        source,
      });
    },
    [posthog],
  );

  const trackPhraseMovedToLearning = useCallback(
    (phraseId: string, phraseText: string, source = "dictionary") => {
      posthog?.capture("phrase_moved_to_learning", {
        phrase_id: phraseId,
        phrase_text: phraseText,
        platform: "web",
        source,
      });
    },
    [posthog],
  );

  const trackPhraseDeleted = useCallback(
    (phraseId: string, phraseText: string, source = "dictionary") => {
      posthog?.capture("phrase_deleted", {
        phrase_id: phraseId,
        phrase_text: phraseText,
        platform: "web",
        source,
      });
    },
    [posthog],
  );

  return {
    trackDictionaryViewed,
    trackDictionaryViewModeChanged,
    trackWordMovedToKnown,
    trackWordMovedToLearning,
    trackWordDeleted,
    trackPhraseMovedToKnown,
    trackPhraseMovedToLearning,
    trackPhraseDeleted,
  };
}
